import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useApi } from 'hooks/useApi';
import styles from './BlogDetail.module.css';

const BlogDetail = () => {
  const { slug } = useParams();
  
  // Environment variable for CMS base URL
  const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

  // API options for useApi hook
  const apiOptions = useMemo(() => ({
    params: { slug }
  }), [slug]);

  // Use the useApi hook
  const { data: blogData, loading, error } = useApi('/blog-details', apiOptions);

  const handleImageError = useCallback((e) => {
    if (e.target.src !== '/Assets/Images/Cases/drone.svg') {
      e.target.src = '/Assets/Images/Cases/drone.svg';
    }
  }, []);

  const galleryItems = useMemo(() => {
    if (!blogData?.gallery) return [];
    
    return blogData.gallery
      .map(item => item?.gallery)
      .filter(imagePath => imagePath && imagePath.trim());
  }, [blogData?.gallery]);

  const sanitizedContent = useMemo(() => {
    if (!blogData?.blog?.description) return '';
    
    return DOMPurify.sanitize(blogData.blog.description, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'class']
    });
  }, [blogData?.blog?.description]);

  const formattedDate = useMemo(() => {
    if (!blogData?.blog?.date) return '';
    
    return new Date(blogData.blog.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [blogData?.blog?.date]);

  const handleGoBack = useCallback(() => {
    window.history.back();
  }, []);

  // Check if author information exists
  const hasAuthorInfo = useMemo(() => {
    const blog = blogData?.blog;
    return blog && (blog.author_name || blog.author_image || blog.author_logo);
  }, [blogData?.blog]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <p>Loading blog...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>Error loading blog: {error}</p>
          <button 
            onClick={handleGoBack} 
            className={styles.backButton}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!blogData || !blogData.blog) {
    return (
      <div className={styles.container}>
        <div className={styles.noDataState}>
          <p>Blog not found</p>
          <button 
            onClick={handleGoBack} 
            className={styles.backButton}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Blog Title */}
      <div className={styles.titleWrapper}>
        <h1 className={styles.title}>{blogData.blog.title}</h1>
        
        {/* Blog Date */}
        {blogData.blog.date && blogData.blog.date.trim() && (
          <div className={styles.dateWrapper}>
            <p className={styles.blogDate}>{formattedDate}</p>
          </div>
        )}

        {/* Blog Sub-heading */}
        {blogData.blog.subheading && blogData.blog.subheading.trim() && (
          <div className={styles.subHeadingWrapper}>
            <p className={styles.subHeading}>{blogData.blog.subheading}</p>
          </div>
        )}
      </div>

      {/* Main Blog Image */}
      {blogData.blog.thumbnail && blogData.blog.thumbnail.trim() && (
        <img 
          src={`${CMS_BASE_URL}/attachments/${blogData.blog.thumbnail}`}
          alt={blogData.blog.title}
          className={styles.image}
          loading="lazy"
          onError={handleImageError}
        />
      )}

      {/* Blog Content/Description */}
      {blogData.blog.description && blogData.blog.description.trim() && (
        <div className={styles.contentWrapper}>
          <div 
            className={styles.blogContent}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        </div>
      )}

      {/* Gallery Images Grid */}
      {galleryItems.length > 0 && (
        <div className={styles.gallerySection}>
          <h3 className={styles.galleryTitle}>Gallery</h3>
          <div className={styles.galleryGrid}>
            {galleryItems.map((item, index) => (
              <div key={`gallery-${index}-${item}`} className={styles.galleryItem}>
                <img
                  src={`${CMS_BASE_URL}/attachments/${item}`}
                  alt={`Gallery image ${index + 1}`}
                  className={styles.galleryImage}
                  loading="lazy"
                  onError={handleImageError}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Author Section */}
      {hasAuthorInfo && (
        <div className={styles.authorSection}>
          <div className={styles.creditsText}>
            <p>Credits to :</p>
          </div>
          <div className={styles.authorCard}>
            {/* Author Image */}
            {blogData.blog.author_image && (
              <div className={styles.authorImageWrapper}>
                <img
                  src={`${CMS_BASE_URL}/attachments/${blogData.blog.author_image}`}
                  alt={blogData.blog.author_name || 'Author'}
                  className={styles.authorImage}
                  onError={handleImageError}
                />
              </div>
            )}
            
            {/* Author Details */}
            <div className={styles.authorInfo}>
              {blogData.blog.author_name && (
                <h4 className={styles.authorName}>{blogData.blog.author_name}</h4>
              )}
              
              {/* Author Logo */}
              {blogData.blog.author_logo && (
                <div className={styles.authorLogoWrapper}>
                  <img
                    src={`${CMS_BASE_URL}/attachments/${blogData.blog.author_logo}`}
                    alt="Author Logo"
                    className={styles.authorLogo}
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetail;